import { SvgIcon } from "@progress/kendo-react-common";
import { Loader } from "@progress/kendo-react-indicators";
import { useInternationalization, type IntlService } from "@progress/kendo-react-intl";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { checkCircleIcon } from "@progress/kendo-svg-icons";
import { forwardRef, useEffect } from "react";
import { useListChatConversationsQuery } from "../api/chatApi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ChatMessageType, type ChatConversationDto } from "../contracts";
import { realtimeEvents } from "../features/signalr/realtimeEvents";
import { useSignalR } from "../features/signalr/useSignalR";
import { clearConversation, clearCurrentCommercialPlan, selectConversation } from "../slices/chatSlice";

const getLastUserMessageSummary = (conversation: ChatConversationDto): string => {
    if (conversation.summary) {
        return conversation.summary;
    }

    const userMessages = conversation.messages.filter(m => m.type === ChatMessageType.User);
    const lastUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1] : undefined;

    const text = lastUserMessage?.text?.trim() ?? "";

    if (!text) {
        return "No user message yet.";
    }

    return text.length > 88 ? `${text.slice(0, 88).trimEnd()}...` : text;
};

const getConversationTitle = (conversation: ChatConversationDto): string => conversation.name?.trim() || `Plan ${conversation.id}`;

const recentConversationsQueryArgs = {
    skip: 0,
    take: 10
};

const isToday = (date: Date): boolean => {
    const now = new Date();
    return date.toDateString() === now.toDateString();
};

const formatConversationDate = (timestamp: Date | string | null | undefined, intl: IntlService): string => {
    if (!timestamp) {
        return "";
    }

    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return intl.formatDate(date, isToday(date) ? "h:mm a" : "MMM d, yyyy");
};

interface RecentConversationsListProps {
    onChange?: (conversationId: number) => void;
}

const RecentConversationsList = forwardRef<HTMLDivElement, RecentConversationsListProps>(({ onChange }, ref) => {
    const intl = useInternationalization();
    const dispatch = useAppDispatch();
    const { data, isLoading, isError, refetch } = useListChatConversationsQuery(recentConversationsQueryArgs);
    const { connection } = useSignalR();
    const currentConversation = useAppSelector(selectConversation);

    useEffect(() => {
        if (!connection) {
            return;
        }

        const handleConversationCreated = (conversation: ChatConversationDto) => {
            void conversation;
            void refetch();
        };
        const handleChatJobUpdated = () => {
            void refetch();
        };

        connection.on(realtimeEvents.conversationCreated, handleConversationCreated);
        connection.on(realtimeEvents.chatJobUpdated, handleChatJobUpdated);

        return () => {
            connection.off(realtimeEvents.conversationCreated, handleConversationCreated);
            connection.off(realtimeEvents.chatJobUpdated, handleChatJobUpdated);
        };
    }, [connection, dispatch, refetch]);

    const conversations = data?.data ?? [];

    return (
        <div ref={ref}>
            <Card className="border-0 shadow-sm mb-2">
                <CardBody className="p-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h2 className="h6 mb-0">Recent Conversations</h2>
                        <span className="badge text-bg-light text-muted">{conversations.length}</span>
                    </div>

                    {isLoading && <p className="mb-0 small text-muted">Loading conversations...</p>}
                    {!isLoading && isError && <p className="mb-0 small text-danger">Unable to load conversations.</p>}
                    {!isLoading && !isError && conversations.length === 0 && <p className="mb-0 small text-muted">No conversations yet.</p>}
                    {!isLoading && !isError && conversations.length > 0 && (
                        <div className="d-flex flex-column gap-2">
                            {conversations.map(conversation => {
                                const isSelected = currentConversation?.id === conversation.id;
                                const timestamp = formatConversationDate(conversation.last_message_at ?? conversation.created_at, intl);
                                const isBusy = conversation.is_busy;

                                return (
                                    <div
                                        key={conversation.id}
                                        className={`position-relative border rounded-3 p-2 pe-4 ${isSelected
                                            ? "border-primary bg-primary-subtle"
                                            : "bg-white"
                                        }`}
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            cursor: "pointer",
                                            transition: "background-color 250ms ease, border-color 250ms ease, box-shadow 250ms ease"
                                        }}
                                        onClick={() => {
                                            dispatch(clearConversation());
                                            dispatch(clearCurrentCommercialPlan());
                                            onChange?.(conversation.id);
                                        }}
                                        onKeyDown={event => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                dispatch(clearConversation());
                                                dispatch(clearCurrentCommercialPlan());
                                                onChange?.(conversation.id);
                                            }
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <p className="mb-0 fw-semibold text-dark">{getConversationTitle(conversation)}</p>
                                            <div className="d-flex align-items-center gap-2 text-nowrap flex-shrink-0">
                                                {timestamp && (
                                                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                        {timestamp}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <p className="mb-0 mt-1 small text-muted">{getLastUserMessageSummary(conversation)}</p>

                                        <span
                                            className="position-absolute bottom-0 end-0 m-2 d-inline-flex align-items-center gap-1"
                                            aria-label={isBusy ? "Conversation busy" : "Conversation available"}
                                            title={isBusy ? "Busy" : "Available"}
                                        >
                                            {isBusy
                                                ? (
                                                        <>
                                                            <Loader size="small" type="infinite-spinner" />
                                                            <span className="text-muted" style={{ fontSize: "0.65rem", lineHeight: 1 }}>
                                                                Busy
                                                            </span>
                                                        </>
                                                    )
                                                : (
                                                        <SvgIcon
                                                            icon={checkCircleIcon}
                                                            size="small"
                                                            className="text-success"
                                                        />
                                                    )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
});

export default RecentConversationsList;
